<script setup lang="ts">
import type { RelatedCompaniesData } from '~/api/related/companies'
import type { RelatedEmployeesData } from '~/api/related/employees'
import DownloadDialog from './related/DownloadDialog.vue'

// 内容区与下载弹窗是多个根节点，父级锚点和样式统一透传到内容区。
defineOptions({ inheritAttrs: false })

const { t } = useI18n()
type InvestigationTab = {
  key: InvestigationTabKey
  label: string
}

type InvestigationTabKey = 'companies' | 'employees'

const props = defineProps<{
  relatedCompanies: RelatedCompaniesData | null
  relatedEmployees: RelatedEmployeesData | null
  relatedCompaniesLoading?: boolean
  relatedEmployeesLoading?: boolean
  activeEmployeeCompanyId?: string
}>()

const emit = defineEmits<{
  'change-employee-company': [companyId: string]
  'load-more-companies': []
  'load-more-employees': []
}>()

/** 当前背景调查内容的 Tab。 */
const activeTabKey = ref<InvestigationTabKey>('companies')

/** 保留接口的分页结果，列表使用 items，数量使用后端 total。 */
const companies = computed(() => props.relatedCompanies?.items || [])
const companyTotal = computed(() => props.relatedCompanies?.total ?? companies.value.length)
const employees = computed(() => props.relatedEmployees?.items || [])
const employeeTotal = computed(() => props.relatedEmployees?.total ?? employees.value.length)
const employeeCompanyFilterOptions = computed(() => props.relatedEmployees?.companyFilterOptions || [])
/** 仅展示接口确认存在数据的背景调查 Tab。 */
const tabs = computed<InvestigationTab[]>(() => [
  ...(companyTotal.value > 0
    ? [
        {
          key: 'companies' as const,
          label: t('82673', ['']),
        },
      ]
    : []),
  ...(employeeTotal.value > 0 || props.relatedEmployeesLoading
    ? [
        {
          key: 'employees' as const,
          label: t('82674', ['']),
        },
      ]
    : []),
])
/** 当前 Tab 数据归零时，回退到仍有数据的首个 Tab。 */
const visibleActiveTabKey = computed<InvestigationTabKey | undefined>(
  () => tabs.value.find((tab) => tab.key === activeTabKey.value)?.key ?? tabs.value[0]?.key,
)
/** 控制 APP 下载弹窗的显示状态。 */
const isDownloadDialogVisible = ref(false)
const isCompaniesExpanded = ref(false)
const hasExpandableCompanies = computed(() => companyTotal.value > 6)
const visibleCompanies = computed(() => (isCompaniesExpanded.value ? companies.value : companies.value.slice(0, 6)))
/** 关联企业只有一条时采用横向信息卡布局。 */
const isSingleCompany = computed(() => companyTotal.value === 1)

/** 切换背景调查内容。 */
const handleTabClick = (tabKey: InvestigationTabKey) => {
  activeTabKey.value = tabKey
}

/** 切换关联企业列表的展开状态，首次展开时请求剩余数据。 */
const handleLoadMoreCompanies = () => {
  if (!hasExpandableCompanies.value || props.relatedCompaniesLoading) {
    return
  }

  if (isCompaniesExpanded.value) {
    isCompaniesExpanded.value = false
    return
  }

  isCompaniesExpanded.value = true
  emit('load-more-companies')
}

/** 打开 APP 下载弹窗。 */
const handleCompanyClick = () => {
  isDownloadDialogVisible.value = true
}

/** 按企业分组筛选关联员工。 */
const handleEmployeeCompanyChange = (companyId: string) => {
  emit('change-employee-company', companyId)
}

/** 展开关联员工时加载后续分页。 */
const handleEmployeeLoadMore = () => {
  emit('load-more-employees')
}

/** 员工卡片点击与关联企业保持一致，打开 APP 下载引导。 */
const handleEmployeeClick = () => {
  isDownloadDialogVisible.value = true
}

/** 关闭 APP 下载弹窗。 */
const handleDownloadDialogClose = () => {
  isDownloadDialogVisible.value = false
}
</script>

<template>
  <section
    v-bind="$attrs"
    class="background-investigation mt-[40px]"
    :class="{ 'background-investigation-single': isSingleCompany }"
    data-exchange-detail-section="background-investigation"
  >
    <div class="background-investigation-card">
      <div
        class="background-tabs"
        :class="{ 'background-tabs-single': tabs.length === 1 }"
        role="tablist"
        aria-label="背景调查"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="background-tab cursor-pointer hover:text-[#292A2E]"
          :class="{ 'background-tab-active': tab.key === visibleActiveTabKey }"
          role="tab"
          :aria-selected="tab.key === visibleActiveTabKey"
          :tabindex="tab.key === visibleActiveTabKey ? 0 : -1"
          @click="handleTabClick(tab.key)"
        >
          <span class="background-tab-label">{{ tab.label }}</span>
          <span v-if="(tab.key === 'companies' ? companyTotal : employeeTotal) >= 2" class="background-tab-count">
            {{ tab.key === 'companies' ? companyTotal : employeeTotal }}
          </span>
        </button>
      </div>

      <div v-if="visibleActiveTabKey === 'companies'" class="mt-[20px]">
        <div class="background-company-grid" :class="{ 'background-company-grid-single': isSingleCompany }">
          <article
            v-for="(company, index) in visibleCompanies"
            :key="company.companyId || `${company.companyName || 'company'}-${index}`"
            class="background-company"
            :class="{ 'background-company-single': isSingleCompany }"
          >
            <ExchangeDetailRelatedCompanies :data="company" :is-single="isSingleCompany" @click="handleCompanyClick" />
          </article>
        </div>

        <button
          v-if="hasExpandableCompanies"
          type="button"
          class="background-expand cursor-pointer hover:text-[#292A2E]"
          :disabled="relatedCompaniesLoading"
          @click="handleLoadMoreCompanies"
        >
          {{ isCompaniesExpanded ? t('83802') : t('84570') }}
        </button>
      </div>
      <div v-else>
        <ExchangeDetailRelatedEmployees
          :active-company-id="activeEmployeeCompanyId || ''"
          :company-filter-options="employeeCompanyFilterOptions"
          :items="employees"
          :loading="relatedEmployeesLoading"
          :total="employeeTotal"
          @change-company="handleEmployeeCompanyChange"
          @employee-click="handleEmployeeClick"
          @load-more="handleEmployeeLoadMore"
        />
      </div>
    </div>
  </section>
  <DownloadDialog :visible="isDownloadDialogVisible" :title="t('88834')" @close="handleDownloadDialogClose" />
</template>

<style scoped lang="less">
.background-investigation {
  --background-text: #292a2e;
  --background-text-subtle: #505258;
  --background-text-subtlest: #7d818a;
  --background-count-bg: #f8f8f8;
  --background-card-bg: #f8f8f8;
  --background-link: #0d3f63;
  --background-muted-label: #86909c;
  --background-muted-value: #3d3d3d;
  --background-border: rgba(5, 21, 36, 0.06);
  --background-divider: rgba(11, 18, 40, 0.14);
  --background-regulated: #1f845a;

  width: 100%;
  min-height: auto;
  color: var(--background-text);
  background: #fff;
}

.background-investigation-single {
  min-height: auto;
}

.background-investigation,
.background-investigation * {
  box-sizing: border-box;
}

.background-investigation-card {
  width: 100%;
  padding: 24px;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px var(--color-border-subtle, rgba(11, 18, 40, 0.14));
}

.background-tabs {
  display: flex;
  height: 44px;
  align-items: center;
  gap: 24px;
  border-bottom: 0.5px solid var(--background-border);
}

.background-tabs-single {
  .background-tab-active::after {
    display: none;
  }
}

.background-tab {
  position: relative;
  display: inline-flex;
  height: 44px;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 0 0 16px;
  border: 0;
  background: transparent;
  color: var(--background-text-subtlest);
  cursor: pointer;
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  white-space: nowrap;
}

.background-tab-active {
  color: var(--background-text);
  font-weight: 600;

  &::after {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 28px;
    height: 4px;
    border-radius: 999px;
    background: var(--background-text);
    content: '';
    transform: translateX(-50%);
  }
}

.background-tab-count {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  margin-top: 5px;
  padding: 0 4px;
  border-radius: 1000px;
  background: var(--background-count-bg);
  color: var(--background-text-subtlest);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.background-company-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 368px));
  gap: 24px;
}

.background-company {
  display: flex;
  width: 368px;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
}

.background-company-grid-single {
  display: block;
}

.background-company-single {
  width: 100%;
}

.company-header {
  display: flex;
  width: 100%;
  min-height: 68px;
  align-items: flex-start;
  gap: 12px;
}

.company-icon {
  display: block;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  object-fit: contain;
  transform: translate(-1px, -1px);
}

.company-identity {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
}

.company-identity h3 {
  display: -webkit-box;
  min-height: 24px;
  margin: 0;
  overflow: hidden;
  color: var(--background-text);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.company-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.company-status {
  color: var(--background-regulated);
}

.company-divider {
  width: 1px;
  height: 8px;
  background: var(--background-divider);
}

.company-country {
  color: var(--background-text-subtle);
}

.company-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  min-height: 78px;
  padding: 16px;
  border-radius: 8px;
  background: var(--background-card-bg);
}

.company-fact {
  min-width: 0;
}

.company-fact p,
.company-fact strong {
  margin: 0;
}

.company-fact p {
  color: var(--background-muted-label);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.company-fact strong {
  display: block;
  margin-top: 4px;
  overflow: hidden;
  color: var(--background-muted-value);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.background-expand {
  display: flex;
  width: 100%;
  height: 52px;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
  border: 0;
  background: transparent;
  color: var(--background-link);
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .background-investigation {
    min-height: auto;
  }

  .background-company-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .background-company {
    width: 100%;
  }
}

@media screen and (max-width: 799px) {
  .background-investigation {
    min-width: 752px;
    min-height: auto;
  }

  .background-company-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .background-company {
    width: 100%;
  }
}
</style>
